/**
 * Template Export/Import Modal
 * Sprint 19: Custom Template Creation
 * 
 * Allows users to export templates to JSON and import templates from JSON files
 */

import React, { useState } from 'react';
import { Modal, Button, Space, Alert, Input, message, Upload, Divider } from 'antd';
import { 
  DownloadOutlined, 
  UploadOutlined, 
  CopyOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { Template } from '../../../../types/template';
import templateService from '../../services/templateService';
import './TemplateExportImportModal.scss';

const { TextArea } = Input;

interface TemplateExportImportModalProps {
  visible: boolean;
  onClose: () => void;
  template?: Template;
  mode: 'export' | 'import';
  onImportSuccess?: (templateId: string) => void;
}

export const TemplateExportImportModal: React.FC<TemplateExportImportModalProps> = ({
  visible,
  onClose,
  template,
  mode,
  onImportSuccess
}) => {
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const [copied, setCopied] = useState(false);

  const getExportData = (): string => {
    if (!template) return '';
    
    const exportTemplate = {
      title: template.title,
      description: template.description,
      category: template.category,
      scenes: template.scenes,
      scripts: template.scripts,
      exampleOutput: template.exampleOutput,
    };
    
    return JSON.stringify(exportTemplate, null, 2);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getExportData());
      setCopied(true);
      message.success('Template copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      message.error('Failed to copy to clipboard');
    }
  };

  const handleDownload = () => {
    const data = getExportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template?.title?.replace(/\s+/g, '-').toLowerCase() || 'template'}-export.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    message.success('Template downloaded!');
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      message.error('Please paste template JSON or upload a file');
      return;
    }

    try {
      setImporting(true);
      const parsed = JSON.parse(importData);
      
      if (!parsed.title || !parsed.scenes) {
        throw new Error('Invalid template format: missing title or scenes');
      }
      
      const newTemplateId = await templateService.createCustomTemplate({
        title: `${parsed.title} (Imported)`,
        description: parsed.description || '',
        category: parsed.category || 'content',
        scenes: parsed.scenes,
        scripts: parsed.scripts || [],
        exampleOutput: parsed.exampleOutput || { 
          title: parsed.title,
          duration: 60,
          whatUserGets: ''
        },
        createdBy: 'user',
        isOfficial: false,
      });
      
      message.success('Template imported successfully!');
      onImportSuccess?.(newTemplateId);
      onClose();
    } catch (error: any) {
      if (error.message.includes('JSON')) {
        message.error('Invalid JSON format. Please check your input.');
      } else {
        message.error(error.message || 'Failed to import template');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setImportData(content);
    };
    reader.onerror = () => {
      message.error('Failed to read file');
    };
    reader.readAsText(file);
    return false;
  };

  const renderExportContent = () => (
    <div className="export-content">
      <Alert
        message="Export Template"
        description="Copy or download your template as JSON to share with others or backup your work."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <TextArea
        value={getExportData()}
        readOnly
        rows={12}
        className="export-textarea"
      />
      
      <Divider />
      
      <Space className="export-actions">
        <Button 
          icon={copied ? <CheckCircleOutlined /> : <CopyOutlined />}
          onClick={handleCopyToClipboard}
        >
          {copied ? 'Copied!' : 'Copy to Clipboard'}
        </Button>
        <Button 
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
        >
          Download JSON
        </Button>
      </Space>
    </div>
  );

  const renderImportContent = () => (
    <div className="import-content">
      <Alert
        message="Import Template"
        description="Paste template JSON below or upload a .json file to import a template."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      
      <Upload.Dragger
        accept=".json"
        showUploadList={false}
        beforeUpload={handleFileUpload}
        className="import-uploader"
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        <p className="ant-upload-text">Click or drag JSON file to upload</p>
      </Upload.Dragger>
      
      <Divider>Or paste JSON below</Divider>
      
      <TextArea
        value={importData}
        onChange={(e) => setImportData(e.target.value)}
        placeholder='{"title": "My Template", "scenes": [...], ...}'
        rows={10}
        className="import-textarea"
      />
    </div>
  );

  return (
    <Modal
      title={mode === 'export' ? 'Export Template' : 'Import Template'}
      open={visible}
      onCancel={onClose}
      width={600}
      className="template-export-import-modal"
      footer={
        mode === 'import' ? (
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button 
              type="primary" 
              loading={importing}
              onClick={handleImport}
              icon={<UploadOutlined />}
            >
              Import Template
            </Button>
          </Space>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      {mode === 'export' ? renderExportContent() : renderImportContent()}
    </Modal>
  );
};

export default TemplateExportImportModal;
